<template>
    <div class="question-version-switcher">
        <div class="question-body">
            <div
                class="question-version-toggle d-flex align-items-center"
                :class="hasPrintView ? 'justify-content-between' : 'justify-content-end'"
            >
                <div v-if="hasPrintView" role="tablist" aria-label="Question versions">
                    <button
                        type="button"
                        class="question-version-toggle-button"
                        :class="{ active: active_version === 'online' }"
                        :aria-pressed="active_version === 'online'"
                        @click="active_version = 'online'"
                    >
                        {{ online_label }}
                    </button>
                    <button
                        v-if="hasPrintView"
                        type="button"
                        class="question-version-toggle-button"
                        :class="{ active: active_version === 'print' }"
                        :aria-pressed="active_version === 'print'"
                        @click="active_version = 'print'"
                    >
                        {{ print_label }}
                    </button>
                </div>

                <CopyToClipboardButton
                    v-if="active_version === 'online' && canCopyOnlineVersion"
                    class="m-3"
                    :value="onlineClipboardValue"
                    :as-html="false"
                    variant="dark"
                    size="sm"
                />

                <CopyToClipboardButton
                    v-if="active_version === 'print' && hasPrintView"
                    class="m-3"
                    :value="question.print_view"
                    :as-html="true"
                    variant="dark"
                    size="sm"
                />
            </div>

            <div v-if="active_version === 'print' && hasPrintView" class="pt-3">
                <div class="print-view-content">
                    <TipTap
                        :key="printViewEditorKey"
                        v-model="question.print_view"
                        :menu_items="[]"
                        :readonly="true"
                    />
                </div>
            </div>
            <div v-else class="pt-3">
                <slot />
            </div>
        </div>
    </div>
</template>

<script>
import CopyToClipboardButton from '@/components/CopyToClipboardButton'
import {exportQuestions} from '@/common/questionsExporter/exportQuestions'

export default {
    data: () => ({
        active_version: 'online',
    }),
    props: {
        question: {
            type: Object,
            required: true,
        },
        warning_title: {
            type: String,
            default: "Print and Online versions don't match",
        },
        warning_text: {
            type: String,
            default: 'Review both versions to ensure consistency.',
        },
        online_label: {
            type: String,
            default: 'Online Version',
        },
        print_label: {
            type: String,
            default: 'Print Version',
        },
    },
    components: {
        TipTap: () => import('@/components/tiptap/TipTap'),
        CopyToClipboardButton
    },
    computed: {
        hasPrintView() {
            return !!(this.question && this.question.print_view)
        },
        onlineClipboardValue() {
            if (!this.question) {
                return ''
            }

            return exportQuestions([this.question])
        },
        canCopyOnlineVersion() {
            return !!this.onlineClipboardValue
        },
        printViewEditorKey() {
            const questionId = this.question && this.question.id ? this.question.id : 'new'
            const printView = this.question && this.question.print_view ? this.question.print_view : ''

            return `${questionId}-${printView}`
        },
    },
    watch: {
        hasPrintView(hasPrintView) {
            if (!hasPrintView) {
                this.active_version = 'online'
            }
        },
    },
}
</script>

<style lang="scss" scoped>
.question-body {
    padding-top: 0.5rem;
}

.print-view-content {
    padding: 0 1rem 1rem;
}

.question-version-toggle {
    border-bottom: 1px solid #dee2e6;
    padding: 0 1rem;
}

.question-version-toggle-button {
    appearance: none;
    border: 0;
    border-bottom: 2px solid transparent;
    background: transparent;
    color: #6c757d;
    padding: 0 1rem 0.5rem 0;
    margin-bottom: -1px;
    font-weight: 600;
    cursor: pointer;
}

.question-version-toggle-button.active {
    color: #007bff;
    border-bottom-color: #007bff;
}

.question-version-toggle-button.active:focus,
.question-version-toggle-button.active:hover {
    color: #007bff;
    border-bottom-color: #007bff;
}

.question-version-toggle-button:not(.active):focus,
.question-version-toggle-button:not(.active):hover {
    border: 0;
    border-bottom: 2px solid transparent;
    background: transparent;
    outline: none;
}
</style>
